
import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { PackageCheckIcon, ShoppingCartIcon } from 'lucide-react'
import { ROUTES } from 'routes'

import { Badge, Box, Stack, useTheme } from '@mui/material'

import { Button, Tabs, TabPanel } from '@repo/ui'
import { Header } from 'components/navigation'
import { PermissionGate } from 'components/access/PermissionGate'
import { ReceivePackageModal } from 'components/feedback/modals/ReceivePackageModal'

import { Inventory, Orders, Shop } from './components'
import { carton } from '@lucide/lab'
import { useCart } from 'contexts/CartContext'

type Filters = {
  limit: number
  offset: number
  search: string | null
}

const DEFAULT_FILTERS: Filters = {
  limit: 10,
  offset: 0,
  search: null,
}

interface TagsPageProps {
  tab?: string
}

export const TagsPage = (props: TagsPageProps) => {
  const { tab } = props
  const { id } = useParams()
  const theme = useTheme();
  const navigate = useNavigate()

  // Cart badge count = total quantity across the cart (0 hides the badge).
  // Was previously a hardcoded `[{},{}]` stub that always showed 2.
  const { itemCount } = useCart()

  // VERA-455: the receipt action the standalone /inventory page used to own. Inventory is a
  // FACET of Tags (tags filtered by possession), not a first-class object, so §8 puts it on
  // this tab rather than in the sidebar — and the action follows the surface it acts on.
  const [receiveModalOpen, setReceiveModalOpen] = useState(false)

  return (
    <>
     <Header
        title={"Tags"}
        rightComponents={
          // No `spacing` here on purpose — Header owns the 12px action-group gap (§29), and a
          // per-page value is exactly the drift that rule exists to stop.
          <Stack direction="row" alignItems="center">
            <Button 
              startIcon={
                <Badge 
                  badgeContent={itemCount}
                  sx={{ 
                    '& .MuiBadge-badge': {
                      background: 'linear-gradient(90deg, rgba(130,90,195,1) 0%, rgba(11,10,103,1) 100%)',
                      color: '#FFFFFF'
                    }
                  }}>
                  <ShoppingCartIcon strokeWidth={1}/>
                </Badge>
              }
              variant="secondary" 
              onClick={() => navigate(ROUTES.TAGS_SHOP_CART)}>
                Cart
            </Button>
            {/* One primary, RIGHTMOST (§26/§33) — and only on the tab it acts on, since
                receiving stock is meaningless while you are browsing the Shop or Orders.
                Gated disable+tooltip, never hidden (§27/§46): a user without the permission
                still learns the capability exists and what to ask an admin for. */}
            {tab === 'inventory' && (
              <PermissionGate permission="tags_inventory_receive">
                <Button
                  variant="primary"
                  startIcon={<PackageCheckIcon strokeWidth={1} />}
                  onClick={() => setReceiveModalOpen(true)}
                >
                  Receive package
                </Button>
              </PermissionGate>
            )}
          </Stack>
      }/>
      <Box>
          <Tabs tabs={[{
            label: 'Inventory',
            value: ROUTES.TAGS_INVENTORY,
          },{
            label: 'Shop',
            value: ROUTES.TAGS_SHOP,
          },{
            label: 'Orders',
            value: ROUTES.TAGS_ORDERS,
          }]}>
            <TabPanel sx={{p: 0, pt: 8}} value={ROUTES.TAGS_INVENTORY}>
              <Inventory />
            </TabPanel>
            <TabPanel  sx={{p: 0, pt: 4}} value={ROUTES.TAGS_SHOP}>
              <Shop />
            </TabPanel>
            <TabPanel  sx={{p: 0, pt: 4}} value={ROUTES.TAGS_ORDERS}>
              <Orders />
            </TabPanel>
          </Tabs>
      </Box>
      <ReceivePackageModal open={receiveModalOpen} onClose={() => setReceiveModalOpen(false)} />
    </>
  )
}